// apps/admin/src/lib/nativeMfa.ts
//
// Thin, typed wrapper around the Firebase modular Web SDK's native
// Multi-Factor Authentication (GCIP) TOTP API for the founder admin portal.
//
// Replaces the bespoke setupMFA / verifyAndEnableMFA / verifyLoginMFA callables
// and the sessionStorage `admin_mfa_verified` gate: the founder's second factor
// is now enrolled and challenged natively at sign-in, so a signed-in founder is
// MFA-verified by construction (they could not have obtained the session token
// without passing the challenge). GCIP allows exactly ONE TOTP factor per
// account, so recovery is an audited admin-SDK break-glass reset, not a spare
// code — there is deliberately no "add another authenticator" affordance.
//
// (Deliberately duplicated per app, mirroring lib/appCheckRecovery.ts, so that
// a client-only or admin-only change never forces an all-codebases deploy.)

import {
  multiFactor,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  FactorId,
  type Auth,
  type User,
  type TotpSecret,
  type MultiFactorResolver,
  type MultiFactorError,
  type MultiFactorInfo,
  type UserCredential,
} from 'firebase/auth';
import { MFA_UNSUPPORTED_FACTOR_CODE } from './mfaErrors';

export const MFA_REQUIRED_CODE = 'auth/multi-factor-auth-required';
export const MFA_FACTOR_DISPLAY_NAME = 'Authenticator app';
export const MFA_ISSUER = 'VALUTTEN';

export function isMfaRequiredError(err: unknown): err is MultiFactorError {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { code?: unknown }).code === MFA_REQUIRED_CODE
  );
}

export function buildMfaResolver(auth: Auth, error: MultiFactorError): MultiFactorResolver {
  return getMultiFactorResolver(auth, error);
}

export function formatSecretKey(secretKey: string): string {
  return (secretKey.match(/.{1,4}/g) ?? [secretKey]).join(' ');
}

export interface TotpEnrollmentInit {
  secret: TotpSecret;
  otpauthUrl: string;
  sharedSecretKey: string;
}

export async function beginTotpEnrollment(user: User): Promise<TotpEnrollmentInit> {
  const mfaUser = multiFactor(user);
  const session = await mfaUser.getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  const accountName = user.email ?? user.uid;
  return {
    secret,
    otpauthUrl: secret.generateQrCodeUrl(accountName, MFA_ISSUER),
    sharedSecretKey: formatSecretKey(secret.secretKey),
  };
}

export async function completeTotpEnrollment(
  user: User,
  secret: TotpSecret,
  oneTimePassword: string,
  displayName: string = MFA_FACTOR_DISPLAY_NAME,
): Promise<void> {
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, oneTimePassword);
  await multiFactor(user).enroll(assertion, displayName);
}

export function getTotpHint(resolver: MultiFactorResolver): MultiFactorInfo | null {
  return resolver.hints.find((hint) => hint.factorId === FactorId.TOTP) ?? null;
}

export async function resolveTotpSignIn(
  resolver: MultiFactorResolver,
  oneTimePassword: string,
): Promise<UserCredential> {
  const hint = getTotpHint(resolver);
  if (!hint) {
    // The code lets MfaChallengeCard tell this dead end apart from a wrong code (M3).
    throw Object.assign(
      new Error('No authenticator is set up for this account. Use the admin break-glass reset.'),
      { code: MFA_UNSUPPORTED_FACTOR_CODE },
    );
  }
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, oneTimePassword);
  return resolver.resolveSignIn(assertion);
}

/** True when the signed-in user already has a TOTP factor enrolled. */
export function hasEnrolledTotp(user: User): boolean {
  return multiFactor(user).enrolledFactors.some((factor) => factor.factorId === FactorId.TOTP);
}

// -- Pending-challenge holder (a live resolver cannot cross router history) ----
//
// The login surface arms it, /mfa/verify reads it, and it is cleared explicitly
// when the challenge completes (VerifyMFAPage) or the founder is back on /login
// (LoginPage mount). Reads are idempotent on purpose: the previous single-use
// `take` was drained by React.StrictMode's double render on mount (finding M4,
// 2026-08-17), which bounced the founder to /login straight after a correct
// password on every dev run. Nothing in render may consume module state.
let pendingResolver: MultiFactorResolver | null = null;

export function setPendingMfaResolver(resolver: MultiFactorResolver): void {
  pendingResolver = resolver;
}

/** Read without consuming; null when nothing is pending (direct visit / refresh). */
export function peekPendingMfaResolver(): MultiFactorResolver | null {
  return pendingResolver;
}

/**
 * Drop the pending resolver: challenge completed, or abandoned back at /login.
 *
 * Compare-and-clear (Codex CX-M4-1, 2026-08-18). Pass the resolver you own and
 * it is cleared ONLY if it is still the pending one: a challenge that was left
 * in flight while a second sign-in armed a fresh resolver can otherwise erase
 * that fresh one before /mfa/verify has read it, stranding the founder at
 * /login. Called with no argument it clears whatever is there, which is what
 * the /login mount wants (any earlier challenge is abandoned by definition).
 *
 * @returns whether the holder was cleared by this call.
 */
export function clearPendingMfaResolver(expected?: MultiFactorResolver): boolean {
  if (expected !== undefined && expected !== pendingResolver) return false;
  pendingResolver = null;
  return true;
}
